// Planner bot (rules 6.1: Flag Conquest, d8 tags; abilities and prisons handled by search).
//
// Search: to pick an activation, the bot lists every end state each of its units can reach this
// activation (a hex and a facing, moving by facing and stopping where an enemy zone stops it), and
// every action it could take there (tag, claim, pick up the flag, use an outpost, stand guard, drop the flag onto our HQ
// hex (it scores), or just end). Each option is played on a copy with the real engine and scored with evaluate(); a tag
// is scored as hit × P(hit) + miss × P(miss), using dice forced to a hit and to a miss. While a unit
// is active, the bot re-runs the same search from where it stands and takes the first step of the
// best option, so a plan always ends where it was meant to.
//
// evaluate() judges a position for one team from facing-aware reach: which hexes each enemy can
// threaten (its guard zone from anywhere it can reach), how many activations a unit needs to reach
// a hex (zones stop it, and from the next round on every enemy zone is up again), the flag race
// (pickup, escape route home and the carrier's survival), unit safety, tempo (activations left this
// round), cells and their effects, domination and the mercy rule. Screening and guard stance
// have value because zones lengthen the enemy's routes and cut its threats.

import { Hex, hexEq, distance, neighbors } from './board';
import {
  applyAction, listLegalActions, actorTeam, playerOf, unitById, other, teamOf, nextRng, moveOf,
  zoneUp, tagChance, territory, canCarry, cellCounts, unitsInCell, claimNeeds, groundDefense, tagBonus, weightOf, DIE, BASE_NEED, effectHeld,
} from './rules';
import { hasTrait } from './units';
import { hqOf, homeCell, mapIndex, cellIdAt } from './map';
import type { CtfAction, CtfPlayer, CtfState, Team, Unit, EffectId } from './types';
import { DSTEP, NO_STOPS, hid, newStamp, onMapTable, pathCost, reach, reachFrom, stepsPaid, stopsFor, zoneOffsets, zoneSet } from './bot/movement';
import { distanceMap, lookup, forwardTurns, mapValue, UNREACHABLE } from './bot/routes';
import { FITTED, FITTED_NAME } from './bot/weights';
import type { DistanceMap } from './bot/routes';
import type { EndState, Step } from './bot/movement';
export type { EndState, Step } from './bot/movement';


// ── Weights ──────────────────────────────────────────────────────────────────

const WIN_VALUE = 3000;               // the game (the scale every other weight is set against)
const TEMPO = 45;                    // an activation still to come this round
const TAG_COST = 35;                 // a unit tagged, before the ground it loses
const ADVANCE = 6;                   // per hex a unit stands from its teleporter (up to ADVANCE_CAP): what a tag takes away
const ADVANCE_CAP = 10;
const KEEPER = 10;                   // per hex a unit that can't carry stands inside KEEPER_RANGE of our flag
const KEEPER_RANGE = 6;
const CELL_VALUE = 20;
const JAIL = 250;                    // a unit in prison: out of the game until freed, so worth far more than a tag
const FRONT = 3;                     // per hex a unit that can carry stands inside FRONT_RANGE of the action
const FRONT_RANGE = 8;               // (the enemy flag or the nearest enemy unit)
const SWIFT_VALUE = 70;              // per point of Move a Swiftness cell gives
// Holding an effect cell, beyond its value as a cell (Swiftness is scaled separately).
const HELD_VALUE: Partial<Record<EffectId, number>> = { watchtower: 45, longShot: 70, blink: 50, magnet: 60 };
const AT_RISK = 0.55;                // share of a held cell's value kept when the enemy can take it straight back
// The flag race (raceTurns): each side's chance of scoring falls off with the activations it needs,
// as exp(-(turns - 1) / RACE_SCALE), and is worth WIN_VALUE × RACE_WEIGHT. Each side counts on its
// own, so every activation the enemy carrier is held up is worth something, most of all near its goal.
export const TUNE = { raceUs: 0.8, raceThem: 0.8, raceScale: 2.5 };
const scoreChance = (turns: number) => Math.exp(-Math.max(0, turns - 1) / TUNE.raceScale);
const REPICKUP = 2.5;                // activations a tagged carrier's side loses getting the flag moving again
const CARRY_RISK = 0.3;
const CARRIER_THREAT = 0.5;          // share of a threat on a carrier counted as a tag (it may run first)
const PICKUP_HIT = 0.5;              // chance a defender covering the flag's hex tags the new carrier
const PICKUP_RISK = 2;               // activations lost when that happens (the flag drops back by our HQ)              // chance a carrier is tagged on each activation it carries
const APPROACH = 4;                  // per hex between our nearest carriers and the enemy flag
// Per hex of our zones between the enemy runner and its goal. 0 until the win-chance fit sets it: a
// guessed 15 lost clearly (38% over 34 pairs against fast@k4), while 0 plays level (49% over 60 pairs).
const SCREEN = 0;
const NEXT_ROUND = 0.5;              // weight of a threat that can only come next round
// Domination: share of a game win counted when a team is this many cells short of the mark (0, 1, 2, 3).
const DOMINATION_SHARE = [0.8, 0.35, 0.12, 0.04];
const MAX_TURNS = 5;
const GUARD_MIN = 5;                 // a guard must be worth this much more than just ending there
const REPLY_K = 4;                   // options re-scored after the enemy's best reply                 // routes longer than this many activations count as this many

// Activations a unit needs to get onto one of `targets` (hids); MAX_TURNS + 1 if it can't. The first
// activation is stopped by the enemy zones that are up now (if the unit can still act this round);
// after that a new round has begun and every enemy zone is up. Bodies don't block (they move).
//
// Read from a route map (bot/routes.ts) built once per decision. The map leaves out the zone of the
// unit whose options are being scored (the mover) when it is on the stopping side, so it serves every
// option; whether the mover's new zone stands in the way is the screening part. An enemy carrier's
// route home is searched exactly, with every zone: blocking it is too important to approximate.
function turnsTo(s: CtfState, u: Unit, targets: Set<number>, targetKey: string, memo: Memo): number {
  if (!u.pos) return MAX_TURNS + 1;
  if (targets.has(hid(u.pos))) return 0;
  const e = other(u.team);
  const thisRound = !u.activated && s.activations[u.team].used < s.activations[u.team].allowed;
  const start = hid(u.pos) * 8 + u.facing;
  const mi = memo.moverIndex;
  const moverStops = mi >= 0 && s.units[mi].team === e;
  if (u.carrying && moverStops) {
    const k = `${start}|${moveOf(s, u)}|${thisRound ? memo.upId[e] : -1}|${memo.allId[e]}|${targetKey}|${hasTrait(s, u, 'slippery') ? 1 : 0}`;
    let hit = memo.turns.get(k);
    if (hit === undefined) {
      hit = forwardTurns(onMapTable(s), moveOf(s, u), start, targets, stopsFor(s, u, thisRound ? memo.up[e] : memo.all[e]), stopsFor(s, u, memo.all[e]));
      memo.turns.set(k, hit);
    }
    return hit;
  }
  return lookup(routeMap(s, u, targets, targetKey, memo).dm, start, thisRound);
}

// The route map for unit u's kind of mover (its Move and slipperiness) onto `targets`, stopped by the
// other team's zones (without the mover's, when it is on that side). Kept for the decision.
function routeMap(s: CtfState, u: Unit, targets: Set<number>, targetKey: string, memo: Memo) {
  const move = moveOf(s, u), slip = hasTrait(s, u, 'slippery');
  const local = `${u.team}${targetKey}|${move}|${slip ? 1 : 0}`;
  const known = memo.mapsHere.get(local);
  if (known) return known;
  const e = other(u.team);
  const mi = memo.moverIndex;
  const skip = mi >= 0 && s.units[mi].team === e ? mi : -1;
  const upL = slip ? -1 : memo.layoutId(e, true, skip), allL = slip ? -1 : memo.layoutId(e, false, skip);
  const key = `${targetKey}|${move}|${slip ? 's' : `${upL}|${allL}`}`;
  let r = memo.maps.get(key);
  if (!r) {
    const first = slip ? NO_STOPS : memo.zoneOf(upL), later = slip ? NO_STOPS : memo.zoneOf(allL);
    r = { dm: distanceMap(onMapTable(s), move, Array.from(targets), first, later), first, later };
    memo.maps.set(key, r);
  }
  memo.mapsHere.set(local, r);
  return r;
}

// Screening: how many hexes of team t's zones stand between the enemy runner and its goal (the enemy
// carrier's way home, or the way of the enemy unit nearest t's flag to it): hexes closer to the goal
// than the runner, by the route map, and within its reach. Each may cost the runner an activation; the
// race leaves the mover's zone out, so this is where it counts.
function screenOf(s: CtfState, t: Team, memo: Memo): number {
  const e = other(t);
  const flag = s.flags[t];
  let runner: Unit | undefined, targets: Set<number>, key: string;
  if (flag.carrierId) {
    runner = unitById(s, flag.carrierId);
    targets = new Set([hid(hqOf(s.cells, e))]); key = `hq${e}`;
  } else {
    const fp = flag.pos!;
    runner = s.units.filter(x => x.team === e && !x.inPrison && x.pos && canCarry(s, x)).sort((a, b) => distance(a.pos!, fp) - distance(b.pos!, fp))[0];
    targets = new Set([hid(fp), ...neighbors(fp).map(hid)]); key = `flag${hid(fp)}`;
  }
  if (!runner?.pos) return 0;
  const dm = routeMap(s, runner, targets, key, memo).dm;
  const rv = mapValue(dm, hid(runner.pos) * 8 + runner.facing);
  if (rv === 0 || rv === UNREACHABLE) return 0;
  const reachHexes = moveOf(s, runner) * rv;
  let n = 0;
  memo.all[t].forEach(h => {
    if (distance(hexOf(h), runner!.pos!) > reachHexes) return;
    for (let f = 0; f < 6; f++) if (mapValue(dm, h * 8 + f) < rv) { n++; return; }
  });
  return n;
}
const hexOf = (h: number): Hex => ({ q: (h >> 6) - 32, r: (h & 63) - 32 });

// ── Threats ──────────────────────────────────────────────────────────────────

// The hexes a unit could tag into on its next activation: its guard zone from every end state it can
// reach (stopped by its enemy's up zones). Cached per unit (its index `i`), position and zone layout.
function threatOf(s: CtfState, e: Unit, i: number, memo: Memo): Set<number> {
  const known = memo.threats[i];
  if (known) return known;
  const foe = other(e.team);
  const active = s.active?.unitId === e.id;
  const moves = active ? (s.active!.moveLocked ? 0 : s.active!.movesLeft) : moveOf(s, e);
  const start = hid(e.pos!) * 8 + e.facing;
  const stops = stopsFor(s, e, memo.up[foe]);
  // Earlier results for this unit, start and Move stand if the stops agree on every hex they asked about.
  const k = (i * 32768 + start) * 64 + moves;
  let found = memo.threat.get(k);
  if (!found) memo.threat.set(k, (found = []));
  const r = reuse(found, stops);
  if (r) return (memo.threats[i] = r);
  const out = new Set<number>();
  const offs = [0, 1, 2, 3, 4, 5].map(f => zoneOffsets(s, e, f));
  const asked: number[] = [];
  for (const st of [start, ...reachFrom(onMapTable(s), moves, stops, [start], newStamp(), asked, newStamp())]) {
    const h = st >> 3;
    for (const d of offs[st & 7]) out.add(h + d);
  }
  found.push({ stops, asked, out });
  return (memo.threats[i] = out);
}

interface Found<T> { stops: Set<number>; asked: number[]; out: T }
// Whether `stops` answers every hex the earlier search asked about the same way.
function agrees(r: Found<unknown>, stops: Set<number>): boolean {
  if (r.stops === stops) return true;
  for (const h of r.asked) if (r.stops.has(h) !== stops.has(h)) return false;
  return true;
}
// A stored result found under these very stops (layouts are shared within a decision), or else under
// stops that agree on every hex it asked about (moved to the front: the next lookup likely wants it too).
function reuse<T>(found: Found<T>[], stops: Set<number>): T | null {
  for (const r of found) if (r.stops === stops) return r.out;
  for (let j = 0; j < found.length; j++) {
    const r = found[j];
    if (!agrees(r, stops)) continue;
    if (j > 0) { found.splice(j, 1); found.unshift(r); }
    return r.out;
  }
  return null;
}

// ── Danger maps ──────────────────────────────────────────────────────────────
// Built once per decision and shared by every option of the unit being tried (the mover), which is
// left out of them and added back per position: which enemy units could strike each hex next
// activation (a bitmask of unit indices), and how many units' zones cover each hex (their weight;
// zones count for tag odds whether or not the unit has acted).

// Which units of t's enemies (not the mover) could strike each hex. The strikers' reach depends on
// their own places, Move (Swiftness held, the flag carried, the active unit's Move left) and t's up
// zones; round and activations keep a map from outliving the state it was built for.
function strikeMap(s: CtfState, t: Team, memo: Memo): Uint16Array {
  const here = memo.strikeHere[t];
  if (here) return here;
  const foe = other(t), mi = memo.moverIndex;
  const skip = mi >= 0 && s.units[mi].team === foe ? mi : -1;
  const key = strikeKey(s, t, memo);
  let m = memo.strikes.get(key);
  if (!m) {
    m = new Uint16Array(64 * 64);
    for (let i = 0; i < s.units.length; i++) {
      const e = s.units[i];
      if (i === skip || e.team !== foe || e.inPrison || !e.pos) continue;
      const bit = 1 << i;
      threatOf(s, e, i, memo).forEach(h => { m![h] |= bit; });
    }
    memo.strikes.set(key, m);
  }
  return (memo.strikeHere[t] = m);
}

function strikeKey(s: CtfState, t: Team, memo: Memo): string {
  const foe = other(t), mi = memo.moverIndex;
  const skip = mi >= 0 && s.units[mi].team === foe ? mi : -1;
  const a = s.active;
  return `${t}|${memo.upId[t]}|${memo.layoutId(foe, false, skip)}|${s.round}|${s.activations.A.used}|${s.activations.B.used}|${effectHeld(s, foe, 'swift')}|${s.flags[t].carrierId ?? ''}|${a ? `${a.unitId}${a.moveLocked ? 0 : a.movesLeft}` : ''}`;
}

// Cover: per hex, the weight of each team's units whose zone covers it, without the mover's zone.
function coverBase(s: CtfState, memo: Memo): Record<Team, Int8Array> {
  const mi = memo.moverIndex;
  const key = `${memo.layoutId('A', false, mi)}|${memo.layoutId('B', false, mi)}`;
  let c = memo.covers.get(key);
  if (!c) {
    c = { A: new Int8Array(64 * 64), B: new Int8Array(64 * 64) };
    s.units.forEach((u, i) => {
      if (i === mi || u.inPrison || !u.pos) return;
      const w = weightOf(s, u), h = hid(u.pos);
      for (const d of zoneOffsets(s, u, u.facing)) c![u.team][h + d] += w;
    });
    memo.covers.set(key, c);
  }
  return c;
}

// This position's cover: the base plus the mover's zone where it now stands (cov reads it).
interface Cover { base: Record<Team, Int8Array>; team: Team | null; zone: number[]; w: number }
function coverOf(s: CtfState, memo: Memo): Cover {
  if (memo.cover) return memo.cover;
  const base = coverBase(s, memo), mi = memo.moverIndex;
  const mu = mi >= 0 ? s.units[mi] : null;
  const c: Cover = { base, team: null, zone: [], w: 0 };
  if (mu && !mu.inPrison && mu.pos) {
    const mh = hid(mu.pos);
    c.team = mu.team; c.w = weightOf(s, mu); c.zone = zoneOffsets(s, mu, mu.facing).map(d => mh + d);
  }
  return (memo.cover = c);
}
const cov = (c: Cover, t: Team, h: number) => c.base[t][h] + (t === c.team && c.zone.includes(h) ? c.w : 0);

// The roll an enemy needs to tag `u`, as the engine's tagBreakdown works it out, in two parts so the
// search can price many attackers and strike hexes quickly from the cover tables (a test checks the
// result against the engine): the target's side (its ground, the attacker's Watchtower there, its
// traits, and per hex next to it, its teammates covering an attacker standing there) and the
// attacker's side (its teammates covering the target, and its own traits).
function targetSide(s: CtfState, u: Unit, cover: Cover) {
  const h = hid(u.pos!), foe = other(u.team);
  const base = BASE_NEED + groundDefense(s, u, u.pos) - tagBonus(s, foe, u.pos) + (hasTrait(s, u, 'tough') ? 1 : 0) - (hasTrait(s, u, 'fragile') ? 1 : 0);
  // u's own zone doesn't count (only its teammates' do).
  const own = zoneOffsets(s, u, u.facing), wu = weightOf(s, u), t = u.team;
  const escortAt = (n: number) => cov(cover, t, n) - (own.includes(n - h) ? wu : 0);
  return { base, escortAt };
}
function attackerSide(s: CtfState, e: Unit, u: Unit, cover: Cover) {
  const h = hid(u.pos!);
  const eOwn = zoneOffsets(s, e, e.facing).some(d => hid(e.pos!) + d === h) ? weightOf(s, e) : 0;
  return (cov(cover, e.team, h) - eOwn) - (hasTrait(s, e, 'clumsy') ? 1 : 0) + (hasTrait(s, e, 'sureStrike') ? 1 : 0);
}
const clampNeed = (n: number) => Math.min(DIE, Math.max(2, n));
const hitOf = (need: number) => (DIE + 1 - need) / DIE;

// The bot's estimate of the roll `e` needs to tag `u` striking from hex `from` (for the test).
export function estimateTagNeed(s: CtfState, e: Unit, u: Unit, from: Hex): number {
  const cover = coverOf(s, memoFor(s, newCaches()));
  const t = targetSide(s, u, cover);
  return clampNeed(t.base + t.escortAt(hid(from)) - attackerSide(s, e, u, cover));
}

// Chance `u` is tagged before it can act again, from the best enemy that could reach it with its
// guard zone (one that has already acted this round counts for less: it comes next round). The
// attacker strikes from the hex next to u that u's teammates cover least. Only the best one counts:
// each enemy that tries spends its activation, and a threat isn't a tag yet.
function hitChance(s: CtfState, u: Unit, memo: Memo): number {
  if (!u.pos) return 0;
  const h = hid(u.pos);
  const foe = other(u.team);
  let mask = strikeMap(s, u.team, memo)[h];
  const mi = memo.moverIndex, mu = mi >= 0 ? s.units[mi] : null;
  const moverStrikes = !!mu && mu.team === foe && !mu.inPrison && !!mu.pos && threatOf(s, mu, mi, memo).has(h);
  if (moverStrikes) mask |= 1 << mi;
  if (!mask) return 0;
  const foeCanAct = s.activations[foe].used < s.activations[foe].allowed;
  const cover = coverOf(s, memo);
  const t = targetSide(s, u, cover), map = onMapTable(s);
  let escort = Infinity;
  for (const d of DSTEP) if (map[h + d]) escort = Math.min(escort, t.escortAt(h + d));
  const base = t.base + (escort === Infinity ? 0 : escort);
  let best = 0;
  for (let i = 0; mask; i++, mask >>= 1) {
    if (!(mask & 1)) continue;
    const e = s.units[i];
    const need = clampNeed(base - attackerSide(s, e, u, cover));
    best = Math.max(best, hitOf(need) * (!e.activated && foeCanAct ? 1 : NEXT_ROUND));
  }
  return best;
}


// ── Evaluation ───────────────────────────────────────────────────────────────

interface Memo {
  up: Record<Team, Set<number>>;      // hexes in each team's up zones (stop movement now)
  all: Record<Team, Set<number>>;     // hexes in each team's zones, up or not (next round)
  upId: Record<Team, number>;        // cache keys: each team's units whose zones are up (unit, position, facing)
  allId: Record<Team, number>;       //   and all of its units
  threats: (Set<number> | undefined)[]; // threatOf for this position, by unit index
  cover?: Cover;                      // per hex: weight of each team's units whose zone covers it
  turns: Map<string, number>;
  threat: Map<number, Found<Set<number>>[]>;
  maps: Map<string, { dm: DistanceMap; first: Set<number>; later: Set<number> }>;
  strikes: Map<string, Uint16Array>;  // danger maps: strikers per hex (strikeMap)
  strikeHere: Partial<Record<Team, Uint16Array>>;   // this position's
  covers: Map<string, Record<Team, Int8Array>>;   // cover without the mover (coverBase)
  mapsHere: Map<string, { dm: DistanceMap; first: Set<number>; later: Set<number> }>;   // this position's lookups
  moverIndex: number;                 // the unit whose options are being scored (-1: none)
  layoutId: (t: Team, onlyUp: boolean, skip?: number) => number;   // a zone layout's id, optionally without one unit
  zoneOf: (id: number) => Set<number>;
}
// Caches shared across the positions scored for one decision: route lengths, threats, and the zone
// layouts seen (numbered, with their hexes).
// `weights`: the score's weights for this decision (a bot can play with fitted ones).
const newCaches = (weights: Record<Feature, number> = WEIGHT) => ({ weights, mover: null as string | null, maps: new Map() as Memo['maps'], strikes: new Map() as Memo['strikes'], covers: new Map() as Memo['covers'], turns: new Map<string, number>(), threat: new Map<number, Found<Set<number>>[]>(), layouts: new Map<string, number>(), zones: [] as Set<number>[] });

function memoFor(s: CtfState, caches: ReturnType<typeof newCaches>): Memo {
  const ids = new Map<string, number>();
  const layout = (t: Team, onlyUp: boolean, skip = -1) => {
    const lk = `${t}${onlyUp ? 1 : 0}${skip}`;
    const known = ids.get(lk);
    if (known !== undefined) return known;
    const sig = (onlyUp ? 'u' : 'a') + s.units.map((u, i) => (i !== skip && u.team === t && u.pos && !u.inPrison && (!onlyUp || zoneUp(s, u)) ? `${i}:${hid(u.pos) * 8 + u.facing}` : '')).join(',');
    let id = caches.layouts.get(sig);
    if (id === undefined) {
      id = caches.zones.length;
      caches.layouts.set(sig, id);
      caches.zones.push(zoneSet(s, t, onlyUp, skip));
    }
    ids.set(lk, id);
    return id;
  };
  const upId = { A: layout('A', true), B: layout('B', true) };
  const allId = { A: layout('A', false), B: layout('B', false) };
  return {
    up: { A: caches.zones[upId.A], B: caches.zones[upId.B] },
    all: { A: caches.zones[allId.A], B: caches.zones[allId.B] },
    upId, allId, threats: [],
    turns: caches.turns, threat: caches.threat, maps: caches.maps, mapsHere: new Map(), strikes: caches.strikes, strikeHere: {}, covers: caches.covers,
    moverIndex: caches.mover ? s.units.findIndex(u => u.id === caches.mover) : -1,
    layoutId: layout, zoneOf: id => caches.zones[id],
  };
}

function urgency(s: CtfState): number {
  const limit = s.config.mercyRounds;
  return limit > 0 ? Math.min(1, s.round / limit) : 0;
}

// ── The score as named parts ─────────────────────────────────────────────────
// A position's score for a team is the sum of weight × (its part − the enemy's part), plus the flag
// race and the optional win conditions. The parts are what a fitted (win-chance) score would weigh;
// `features` returns them for data collection.

const PARTS = [
  'tempo',          // activations still to come this round
  'approach',       // hexes between our carrier and home, or our two nearest carriers and the enemy flag (the second counts half)
  'advance',        // per unit that can carry: hexes from its teleporter (up to ADVANCE_CAP)
  'keeper',         // per unit that can't carry: how far inside KEEPER_RANGE of our flag it stands
  'riskTag',        // chance of a tag, summed over units (not the carrier)
  'riskAdvance',    // ... each times its advance
  'riskKeeper',     // ... each times its keeper value
  'riskTempo',      // ... each that hasn't acted yet (it would lose its activation)
  'riskJail',       // ... each standing on ground the enemy holds, with prisons (a tag there jails it)
  'jailed',         // units in prison
  'front',          // per unit that can carry (not the carrier): how far inside FRONT_RANGE of the action it stands
  'cells',          // cells held (not home cells) that the enemy can't take straight back
  'cellsAtRisk',    // cells held that it can
  'swift',          // Swiftness cells held: Move given (counted at AT_RISK when at risk)
  'watchtower',     // other effect cells held (the same)
  'longShot',
  'blink',
  'magnet',
  'screen',         // hexes of our zones on the enemy runner's best routes (its way home, or to our flag)
] as const;
type Part = typeof PARTS[number];
const PART_WEIGHT: Record<Part, number> = {
  tempo: TEMPO, approach: -APPROACH, advance: ADVANCE, keeper: KEEPER,
  riskTag: -TAG_COST, riskAdvance: -ADVANCE, riskKeeper: -KEEPER, riskTempo: -TEMPO,
  riskJail: -JAIL, jailed: -JAIL, front: FRONT,
  cells: CELL_VALUE, cellsAtRisk: CELL_VALUE * AT_RISK,
  swift: SWIFT_VALUE, watchtower: HELD_VALUE.watchtower ?? 0, longShot: HELD_VALUE.longShot ?? 0, blink: HELD_VALUE.blink ?? 0, magnet: HELD_VALUE.magnet ?? 0,
  screen: SCREEN,
};

function sideParts(s: CtfState, t: Team, memo: Memo): Record<Part, number> {
  const f = Object.fromEntries(PARTS.map(k => [k, 0])) as Record<Part, number>;
  const e = other(t);
  const hq = hqOf(s.cells, t);
  const tele = homeCell(s.cells, t, 'teleporter').centre;
  const mine = s.units.filter(u => u.team === t && !u.inPrison && u.pos);
  f.tempo = s.activations[t].allowed - s.activations[t].used;
  f.jailed = s.units.filter(u => u.team === t && u.inPrison).length;

  // Approach: a small pull for the carriers towards the enemy flag (the race below moves in whole
  // activations, so this breaks ties within one).
  const ef = s.flags[e];
  if (ef.carrierId) {
    f.approach = distance(unitById(s, ef.carrierId).pos!, hq);
  } else if (ef.pos) {
    const fp = ef.pos;
    const ds = mine.filter(u => canCarry(s, u)).map(u => distance(u.pos!, fp)).sort((a, b) => a - b);
    f.approach = (ds[0] ?? 0) + 0.5 * (ds[1] ?? 0);
  }

  // Ground: units further from their teleporter are worth more, since a tag sends them back there. A
  // unit that can't carry (the Bulwark) has no use for ground: its post is by our flag (or the enemy
  // carrying it). Unit safety: the chance of a tag (and of losing the activation, if it hasn't gone yet).
  // Engagement: units that can carry are drawn towards the action (the enemy flag or the nearest
  // enemy), so none idles at the back.
  const ownFlag = s.flags[t].carrierId ? unitById(s, s.flags[t].carrierId!).pos : s.flags[t].pos;
  const foes = s.units.filter(u => u.team === e && !u.inPrison && u.pos).map(u => u.pos!);
  const action = ef.pos ? [ef.pos, ...foes] : foes;
  for (const u of mine) {
    const carries = canCarry(s, u);
    const adv = carries ? Math.min(ADVANCE_CAP, distance(u.pos!, tele)) : 0;
    const keep = !carries && ownFlag ? Math.max(0, KEEPER_RANGE - distance(u.pos!, ownFlag)) : 0;
    f.advance += adv;
    f.keeper += keep;
    if (u.carrying) continue;
    if (carries && action.length) f.front += Math.max(0, FRONT_RANGE - Math.min(...action.map(h => distance(u.pos!, h))));
    const p = hitChance(s, u, memo);
    if (!p) continue;
    f.riskTag += p;
    f.riskAdvance += p * adv;
    f.riskKeeper += p * keep;
    if (s.config.prisons && territory(s, u.pos!) === e) f.riskJail += p;
    if (!u.activated && s.activations[t].used < s.activations[t].allowed) f.riskTempo += p;
  }

  // Cells: each counts for the mercy rule and holding, and may carry an effect. A cell the enemy can
  // take straight back is worth less.
  const { adj } = mapIndex(s.cells);
  for (const c of s.cells) {
    if (c.home || s.owners[c.id] !== t) continue;
    const touchesEnemy = Array.from(adj[c.id]).some(n => s.owners[n] === e);
    const raiders = touchesEnemy ? s.units.filter(u => u.team === e && !u.inPrison && u.pos && distance(u.pos, c.centre) <= moveOf(s, u) + 1).length : 0;
    const risky = raiders > 0 && raiders >= claimNeeds(unitsInCell(s, c.id)[t]);
    if (risky) f.cellsAtRisk++; else f.cells++;
    const fx = s.effects[c.id], share = risky ? AT_RISK : 1;
    if (fx === 'swift') f.swift += s.config.swiftBonus * share;
    else if (fx) f[fx] += share;
  }
  f.screen = screenOf(s, t, memo);
  return f;
}

// The flag race: how many of its own activations team t needs to score the enemy flag. A carrier
// needs its route home (through the enemy's zones); if it may be tagged, the flag drops where it
// stands and the race restarts from there: a teammate right next to it picks it up at once, while a
// flag with no one near (or behind the enemy's zones) is stranded for a while. Otherwise the best
// carrier needs its route to the flag (the pickup ends that activation) and the carry home, which
// risks a tag on each activation.
function raceTurns(s: CtfState, t: Team, memo: Memo): number {
  const e = other(t);
  const hq = hqOf(s.cells, t);
  const ef = s.flags[e];
  if (ef.carrierId) {
    const c = unitById(s, ef.carrierId);
    // Its own route, or a hand-off: put the flag down (that activation) for a faster teammate.
    const run = Math.min(turnsTo(s, c, new Set([hid(hq)]), `hq${t}`, memo), 1 + raceFrom(s, t, c.pos!, c.id, memo));
    // A threat on the carrier counts for less than a tag: until someone actually tags it, it runs.
    const h = hitChance(s, c, memo) * CARRIER_THREAT;
    return h ? (1 - h) * run + h * Math.max(run, raceFrom(s, t, c.pos!, c.id, memo)) : run;
  }
  return raceFrom(s, t, ef.pos!, null, memo);
}

// Activations team t needs to pick up a flag lying at fp and carry it home (`skip`: a unit that
// can't take part, such as a carrier just tagged away).
function raceFrom(s: CtfState, t: Team, fp: Hex, skip: string | null, memo: Memo): number {
  const hq = hqOf(s.cells, t);
  const near = new Set([hid(fp), ...neighbors(fp).map(hid)]);
  let best = Infinity;
  // The two would-be carriers nearest the flag (the others are rarely the fastest).
  const carriers = s.units.filter(u => u.team === t && u.id !== skip && !u.inPrison && u.pos && canCarry(s, u))
    .sort((x, y) => distance(x.pos!, fp) - distance(y.pos!, fp)).slice(0, 2);
  // A defender that can strike at the flag's hex makes the pickup risky: picking up ends the
  // activation, so the new carrier stands there to be tagged, deep in enemy ground.
  let guarded = 0;
  for (let i = 0; i < s.units.length; i++) {
    const d = s.units[i];
    if (d.team === t || d.inPrison || !d.pos) continue;
    if (threatOf(s, d, i, memo).has(hid(fp))) guarded = PICKUP_HIT;
  }
  // The carry home goes at the pace of the team's fastest carrier: a slow picker hands the flag
  // over (put it down, the fast one picks it up: one more activation).
  const carryMoveOf = (u: Unit) => Math.max(1, moveOf(s, { ...u, carrying: 'flag' }) - 1);
  const fastest = Math.max(1, ...s.units.filter(u => u.team === t && u.id !== skip && !u.inPrison && canCarry(s, u)).map(carryMoveOf));
  for (const u of carriers) {
    const pick = Math.max(1, turnsTo(s, u, near, `flag${hid(fp)}`, memo));
    const handOff = carryMoveOf(u) < fastest ? 1 : 0;
    const home = Math.max(1, Math.ceil((distance(fp, hq) - 1) / fastest));
    best = Math.min(best, pick + handOff + home + home * CARRY_RISK * REPICKUP + guarded * PICKUP_RISK);
  }
  return best === Infinity ? MAX_TURNS * 3 : best;
}

// The parts for `team` (its own minus the enemy's), the race and the win conditions, as one record.
export const FEATURES = [...PARTS, 'raceUs', 'raceThem', 'mercy', 'domination'] as const;
export type Feature = typeof FEATURES[number];
export const WEIGHT: Record<Feature, number> = {
  ...PART_WEIGHT,
  raceUs: WIN_VALUE * TUNE.raceUs, raceThem: -WIN_VALUE * TUNE.raceThem,
  mercy: WIN_VALUE * (2 / 3), domination: WIN_VALUE,
};

function featuresWith(s: CtfState, team: Team, caches: ReturnType<typeof newCaches>): Record<Feature, number> {
  const memo = memoFor(s, caches);
  const e = other(team);
  const us = sideParts(s, team, memo), them = sideParts(s, e, memo);
  const f = {} as Record<Feature, number>;
  for (const k of PARTS) f[k] = us[k] - them[k];
  // Whoever is closer to bringing the flag home is likelier to win.
  f.raceUs = scoreChance(raceTurns(s, team, memo));
  f.raceThem = scoreChance(raceTurns(s, e, memo));
  const c = cellCounts(s);
  // The cell lead decides the game under the mercy rule.
  f.mercy = s.config.mercyRounds > 0 ? Math.sign(c[team] - c[e]) * Math.pow(urgency(s), 2) : 0;
  // Domination: holding enough cells at the end of a round wins the game. Being at the mark during
  // the round is most of the way there (the enemy can still take cells back before it ends).
  const { dominationCells } = s.config;
  const share = (t: Team) => DOMINATION_SHARE[Math.max(0, dominationCells - c[t])] ?? 0;
  f.domination = dominationCells > 0 ? share(team) - share(e) : 0;
  return f;
}

export function features(s: CtfState, team: Team): Record<Feature, number> {
  return featuresWith(s, team, newCaches());
}

function evaluateWith(s: CtfState, team: Team, caches: ReturnType<typeof newCaches>): number {
  if (s.phase === 'complete') return s.winner === team ? 1e6 : s.winner ? -1e6 : 0;
  const f = featuresWith(s, team, caches);
  let v = 0;
  for (const k of FEATURES) v += caches.weights[k] * f[k];
  return v;
}

export function evaluate(s: CtfState, team: Team): number {
  return evaluateWith(s, team, newCaches());
}

// ── Search ───────────────────────────────────────────────────────────────────

// Seeds whose first die roll is a given number, to play an action with a forced hit or miss.
const DIE_SEED: number[] = (() => {
  const out: number[] = [];
  for (let seed = 1; out.filter(x => x !== undefined).length < DIE; seed++) {
    const d = nextRng(seed, 1, DIE)[0];
    if (out[d] === undefined) out[d] = seed;
  }
  return out;
})();

// An activation option: the path, the final action, its value, and the positions it can lead to
// (two for a tag: hit and miss) with their chances, for the reply search.
// `pre`: an army ability used before the path (Sprint, Rally); `startMoves`/`start`: the unit's Move left
// and place once it has been used (where the path starts).
interface Option { unitId: string; path: Step[]; action: CtfAction; value: number; outcomes: { p: number; s: CtfState }[]; pre: CtfAction[]; startMoves: number; start: { pos: Hex; facing: number } }

// The position with the active unit moved to an end state (path already walked).
function placed(s: CtfState, u: Unit, st: EndState): CtfState {
  const a = s.active!;
  return {
    ...s,
    units: s.units.map(x => (x.id === u.id ? { ...x, pos: st.pos, facing: st.facing } : x)),
    active: { ...a, movesLeft: a.movesLeft - pathCost(st.path), moved: a.moved || st.path.length > 0, moveLocked: a.moveLocked || st.stopped },
  };
}

// Value of a position after our action, resolving a decision of our own that it opened.
function valueAfter(s: CtfState, team: Team, caches: ReturnType<typeof newCaches>, depth = 0): number {
  const p = s.pending;
  // A tag roll waiting for Reroll or Keep (army ability): the side deciding takes its better choice.
  if (p?.kind === 'reroll' && depth < 3) {
    const pid = playerOf(s, p.team).id;
    const keep = actionValue(s, { type: 'keepRoll', playerId: pid }, team, caches, depth + 1);
    const again = actionValue(s, { type: 'reroll', playerId: pid }, team, caches, depth + 1);
    return p.team === team ? Math.max(keep, again) : Math.min(keep, again);
  }
  if (p && p.team === team && depth < 2) {
    const pid = playerOf(s, team).id;
    let best = -Infinity;
    for (const a of listLegalActions(s, pid)) best = Math.max(best, actionValue(s, a, team, caches, depth + 1));
    if (best > -Infinity) return best;
  }
  return evaluateWith(s, team, caches);
}

// The positions one action can lead to: a tag (or long shot) hits or misses, with its chance.
function outcomesOf(s: CtfState, a: CtfAction): { p: number; s: CtfState }[] {
  if (a.type === 'reroll' && s.pending?.kind === 'reroll') {
    const p = (DIE + 1 - s.pending.need) / DIE;
    return [{ p, s: applyAction({ ...s, rng: DIE_SEED[DIE] }, a) }, { p: 1 - p, s: applyAction({ ...s, rng: DIE_SEED[1] }, a) }];
  }
  if (a.type === 'tag' || a.type === 'longShot') {
    const attacker = unitById(s, a.type === 'tag' ? s.active!.unitId : (s.pending as { unitId: string }).unitId);
    const p = tagChance(s, attacker, unitById(s, a.targetId));
    return [{ p, s: applyAction({ ...s, rng: DIE_SEED[DIE] }, a) }, { p: 1 - p, s: applyAction({ ...s, rng: DIE_SEED[1] }, a) }];
  }
  const next = applyAction(s, a);
  return next === s ? [] : [{ p: 1, s: next }];
}

// Expected value of one action.
function actionValue(s: CtfState, a: CtfAction, team: Team, caches: ReturnType<typeof newCaches>, depth = 0): number {
  const outs = outcomesOf(s, a);
  return outs.length ? outs.reduce((v, o) => v + o.p * valueAfter(o.s, team, caches, depth), 0) : -Infinity;
}

const FINAL: CtfAction['type'][] = ['tag', 'pickUp', 'claim', 'dropFlag', 'pass', 'jailbreak', 'useOutpost', 'guard', 'endActivation'];
const REPLY_PASSES: CtfAction['type'][][] = [['tag', 'pickUp', 'claim', 'dropFlag', 'pass', 'jailbreak'], ['useOutpost', 'guard', 'endActivation']];

// Trimming: a cheap ranking of a unit's end states (no search, no full score), so only the most
// promising are scored in full. Options are grouped by what they do: "end here or stand guard here"
// (quiet), and each distinct aim (tag this unit, claim this cell, pick up the flag, ...). A quiet
// group keeps its best `trim` end states, each aim its best `aimed`; every aim is still tried.
const CARRIER_STEP = 60;             // cheap ranking: per hex between a carrier and home
const DEFEND = 4;                    // per hex between a unit and the enemy carrier
const ESCORT = 12;                   // per hex between a unit and our own carrier (its zone walls off the carrier's route)

function cheapRanker(s: CtfState, u: Unit, team: Team, caches: ReturnType<typeof newCaches>) {
  const memo = memoFor(s, caches);
  const e = other(team);
  const foeCanAct = s.activations[e].used < s.activations[e].allowed;
  const threats: { hexes: Set<number>; w: number }[] = [];
  s.units.forEach((x, i) => { if (x.team === e && !x.inPrison && x.pos) threats.push({ hexes: threatOf(s, x, i, memo), w: !x.activated && foeCanAct ? 1 : NEXT_ROUND }); });
  const hq = hqOf(s.cells, team), tele = homeCell(s.cells, team, 'teleporter').centre;
  const ef = s.flags[e], of = s.flags[team];
  const enemyFlag = ef.carrierId ? null : ef.pos;
  const ownFlag = of.carrierId ? unitById(s, of.carrierId).pos : of.pos;
  const enemyCarrier = of.carrierId ? unitById(s, of.carrierId).pos : null;
  const ourCarrier = ef.carrierId && ef.carrierId !== u.id ? unitById(s, ef.carrierId).pos : null;
  const carries = canCarry(s, u);
  return (st: EndState): number => {
    const h = hid(st.pos);
    let danger = 0;
    for (const t of threats) if (t.w > danger && t.hexes.has(h)) danger = t.w;
    let v: number, stake: number;
    if (u.carrying) { v = -CARRIER_STEP * distance(st.pos, hq); stake = 0.5 * WIN_VALUE * CARRIER_THREAT; }
    else if (carries) {
      const adv = Math.min(ADVANCE_CAP, distance(st.pos, tele));
      v = ADVANCE * adv - (enemyFlag ? APPROACH * distance(st.pos, enemyFlag) : 0);
      stake = TAG_COST + ADVANCE * adv;
    } else {
      const keep = ownFlag ? Math.max(0, KEEPER_RANGE - distance(st.pos, ownFlag)) : 0;
      v = KEEPER * keep;
      stake = TAG_COST + KEEPER * keep;
    }
    if (enemyCarrier) v -= DEFEND * distance(st.pos, enemyCarrier);
    if (ourCarrier) v -= ESCORT * distance(st.pos, ourCarrier);
    return v - 0.5 * danger * stake;
  };
}

// Every end state and action for one active unit, scored. Stops early once an option reaches
// `stopAt` (the reply search only needs to know the best is at least that good), and can be limited
// to some final actions and trimmed (above).
function optionsFor(s: CtfState, team: Team, caches: ReturnType<typeof newCaches>, stopAt = Infinity, finals = FINAL, trim = Infinity, aimed = Infinity): Option[] {
  const a = s.active!;
  const u = unitById(s, a.unitId);
  caches.mover = u.id;
  const pid = playerOf(s, team).id;
  const stops = zoneSet(s, other(team), true);
  const states: EndState[] = a.moveLocked ? [{ pos: u.pos!, facing: u.facing, path: [], stopped: true }] : reach(s, u, a.movesLeft, stops);
  const at = { startMoves: a.movesLeft, start: { pos: u.pos!, facing: u.facing }, pre: [] as CtfAction[] };
  const out: Option[] = [];
  const hq = hqOf(s.cells, team);
  // The candidates: each end state's final actions (a scoring walk is one of its own).
  type Cand = { st: number; act: CtfAction | null; hyp: CtfState | null };
  const cands: Cand[] = [];
  states.forEach((st, k) => {
    // A carrier stepping onto its own HQ hex scores there and then.
    if (u.carrying === 'flag' && hexEq(st.pos, hq)) {
      if (finals !== REPLY_PASSES[1]) cands.push({ st: k, act: null, hyp: null });   // scoring counts as forceful
      return;
    }
    const hyp = placed(s, u, st);
    for (const act of listLegalActions(hyp, pid)) {
      if (!finals.includes(act.type)) continue;
      // Of the flag drops, only the one that scores (onto our HQ hex, past a unit standing on it).
      if (act.type === 'dropFlag' && !hexEq(act.to, hq)) continue;
      cands.push({ st: k, act, hyp });
    }
  });
  let keep: (c: Cand) => boolean = () => true;
  if (trim < states.length || aimed < states.length) {
    const rank = cheapRanker(s, u, team, caches);
    const cheap = states.map(rank);
    const groupOf = (c: Cand) => {
      if (!c.act) return 'score';
      switch (c.act.type) {
        case 'endActivation': case 'guard': return 'quiet';
        case 'tag': case 'longShot': return `${c.act.type}:${c.act.targetId}`;
        case 'claim': return `claim:${cellIdAt(s.cells, states[c.st].pos)}`;
        default: return c.act.type;
      }
    };
    const groups = new Map<string, Set<number>>();
    for (const c of cands) { const g = groupOf(c); if (!groups.has(g)) groups.set(g, new Set()); groups.get(g)!.add(c.st); }
    const kept = new Map<string, Set<number>>();
    groups.forEach((sts, g) => {
      const limit = g === 'quiet' ? trim : g === 'score' ? Infinity : aimed;
      kept.set(g, new Set(Array.from(sts).sort((x, y) => cheap[y] - cheap[x] || x - y).slice(0, limit)));
    });
    keep = c => kept.get(groupOf(c))!.has(c.st);
  }
  let k = 0;
  while (k < cands.length) {
    const st = states[cands[k].st];
    let guardAt = -1, endValue = -Infinity;
    for (; k < cands.length && states[cands[k].st] === st; k++) {
      const c = cands[k];
      if (!keep(c)) continue;
      if (!c.act) {
        // Walk the scoring path for real.
        let x = s;
        for (const step of st.path) x = applyAction(x, step.kind === 'move' ? { type: 'move', playerId: pid, to: step.to } : { type: 'turn', playerId: pid, dir: step.dir });
        out.push({ unitId: u.id, path: st.path, action: { type: 'endActivation', playerId: pid }, value: valueAfter(x, team, caches), outcomes: [{ p: 1, s: x }], ...at });
        if (out[out.length - 1].value >= stopAt) return out;
        continue;
      }
      const outcomes = outcomesOf(c.hyp!, c.act);
      if (!outcomes.length) continue;
      const value = outcomes.reduce((v, o) => v + o.p * valueAfter(o.s, team, caches), 0);
      if (c.act.type === 'guard') guardAt = out.length;
      if (c.act.type === 'endActivation') endValue = value;
      out.push({ unitId: u.id, path: st.path, action: c.act, value, outcomes, ...at });
      if (value >= stopAt) return out;
    }
    // Stand guard only where it holds something up: otherwise just end there.
    if (guardAt >= 0 && out[guardAt].value < endValue + GUARD_MIN) out.splice(guardAt, 1);
  }
  return out;
}

// ── Decisions ────────────────────────────────────────────────────────────────

// The value of a position for `team` after the enemy's best activation from it (if the enemy acts
// next): the enemy's own one-activation search, and evaluate() is antisymmetric, so our value is
// minus the enemy's best. Once that is no more than `floor` the search stops (the position is
// already no better than one we have), and the enemy unit that replied best last time goes first.
function replyValue(x: CtfState, team: Team, caches: ReturnType<typeof newCaches>, floor = -Infinity, killer?: { unitId?: string }): number {
  const foe = other(team);
  if (x.phase !== 'playing' || x.pending || actorTeam(x) !== foe) return valueAfter(x, team, caches);
  const pid = playerOf(x, foe).id;
  const acts = listLegalActions(x, pid).filter((a): a is Extract<CtfAction, { type: 'activate' }> => a.type === 'activate');
  if (killer?.unitId) acts.sort((a, b) => Number(b.unitId === killer.unitId) - Number(a.unitId === killer.unitId));
  let best = -Infinity;
  // Forceful replies first (they are the likeliest best, so the search can stop sooner), then the rest.
  search: for (const finals of REPLY_PASSES) {
    for (const a of acts) {
      for (const o of optionsFor(applyAction(x, a), foe, caches, -floor, finals)) {
        if (o.value <= best) continue;
        best = o.value;
        if (killer) killer.unitId = o.unitId;
      }
      if (-best <= floor) break search;
    }
  }
  return best === -Infinity ? evaluateWith(x, team, caches) : -best;
}

// Our best activation: every option of every unit scored one activation deep, then the most
// promising `replyK` re-scored after the enemy's best reply (none: the best one-deep option).
function decide(s: CtfState, team: Team, caches: ReturnType<typeof newCaches>, replyK = REPLY_K, trim = Infinity, aimed = Infinity, explore = 0): Option | null {
  const all: Option[] = [];
  const pid = playerOf(s, team).id;
  // An active unit's options, and those after an army ability it can use first (Sprint, Rally).
  const withAbilities = (x: CtfState) => {
    all.push(...optionsFor(x, team, caches, Infinity, FINAL, trim, aimed));
    for (const pre of listLegalActions(x, pid)) {
      if (pre.type !== 'sprint' && pre.type !== 'rally') continue;
      for (const o of optionsFor(applyAction(x, pre), team, caches, Infinity, FINAL, trim, aimed)) all.push({ ...o, pre: [pre] });
    }
  };
  if (s.active) withAbilities(s);
  else for (const a of listLegalActions(s, pid)) if (a.type === 'activate') withAbilities(applyAction(s, a));
  if (!all.length) return null;
  all.sort((x, y) => y.value - x.value || x.path.length - y.path.length);
  if (explore > 0 && !s.active) {
    // A draw from the dice state and the log length: the same game always explores the same way.
    const [roll] = nextRng(s.rng ^ Math.imul(s.log.length + 7, 0x2545F491), 0, 9999);
    if (roll < explore * 10000) return all[Math.min(all.length - 1, 1 + (roll % 2))];
  }
  if (replyK <= 0) return all[0];
  const top = all.slice(0, replyK);
  let best = top[0], bestV = -Infinity;
  const killer: { unitId?: string } = {};
  for (const o of top) {
    // Only the last outcome can be cut short: the ones before it are needed in full.
    let v = 0;
    for (let i = 0; i < o.outcomes.length; i++) {
      const oc = o.outcomes[i];
      const last = i === o.outcomes.length - 1;
      const floor = last && bestV > -Infinity ? (bestV + 1e-9 - v) / oc.p : -Infinity;
      v += oc.p * replyValue(oc.s, team, caches, floor, killer);
    }
    if (v > bestV + 1e-9) { bestV = v; best = o; }
  }
  return best;
}

// The plan being carried out by each team: the unit, its path and final action, and the Move it had
// when the plan was made (so the step to take next is known). A new search runs if anything differs.
// The dice state (unchanged by moves and turns) and the bot's settings tie a plan to its game and
// its bot, so a plan left over from another game or bot is never followed.
interface Plan { rng: number; bot: string; round: number; unitId: string; pre: CtfAction[]; startMoves: number; start: { pos: Hex; facing: number }; path: Step[]; action: CtfAction }
const plans: Partial<Record<Team, Plan>> = {};

function stepAction(st: Step, playerId: string): CtfAction {
  return st.kind === 'move' ? { type: 'move', playerId, to: st.to } : { type: 'turn', playerId, dir: st.dir };
}

function followPlan(s: CtfState, team: Team, playerId: string, bot: string): CtfAction | null {
  const plan = plans[team];
  const a = s.active;
  if (!plan || !a || plan.rng !== s.rng || plan.bot !== bot || plan.unitId !== a.unitId || plan.round !== s.round) return null;
  // First the army ability the plan starts with (Sprint, Rally), if not used yet.
  const pre = plan.pre[0];
  if (pre && !(pre.type === 'sprint' ? a.sprinted : a.moved)) {
    const p = { ...pre, playerId } as CtfAction;
    return isLegalHere(s, p) ? p : null;
  }
  // How far along the path the unit is: the steps its spent Move has paid for.
  const done = stepsPaid(plan.path, plan.startMoves - a.movesLeft);
  if (done < 0) return null;
  // The unit must be where the plan says it would be by now.
  let at = plan.start;
  for (const st of plan.path.slice(0, done)) at = st.kind === 'move' ? { pos: st.to, facing: at.facing } : { pos: at.pos, facing: (at.facing + (st.dir === 1 ? 1 : 5)) % 6 };
  const u = unitById(s, a.unitId);
  if (!u.pos || !hexEq(u.pos, at.pos) || u.facing !== at.facing) return null;
  const next = done < plan.path.length ? stepAction(plan.path[done], playerId) : { ...plan.action, playerId } as CtfAction;
  return isLegalHere(s, next) ? next : null;
}

const isLegalHere = (s: CtfState, a: CtfAction) => applyAction(s, a) !== s;

// Every activation option at a choice point, scored by the full fast bot (no trimming, no reply
// check): the unit, where it ends and how it faces, the final action and its target. The bench uses
// it to price other bots' choices ("regret": how much worse than the best, by this score).
export function optionValues(s: CtfState): { unit: string; to: Hex; facing: number; action: CtfAction['type']; target: string | null; value: number }[] {
  const team = actorTeam(s);
  if (!team || s.active || s.pending) return [];
  const caches = newCaches(), pid = playerOf(s, team).id;
  const out: ReturnType<typeof optionValues> = [];
  for (const a of listLegalActions(s, pid)) {
    if (a.type !== 'activate') continue;
    const u = unitById(s, a.unitId);
    for (const o of optionsFor(applyAction(s, a), team, caches)) {
      let at = { pos: u.pos!, facing: u.facing };
      for (const st of o.path) at = st.kind === 'move' ? { pos: st.to, facing: at.facing } : { pos: at.pos, facing: (at.facing + (st.dir === 1 ? 1 : 5)) % 6 };
      const target = o.action.type === 'tag' || o.action.type === 'longShot' ? o.action.targetId : null;
      out.push({ unit: u.id, to: at.pos, facing: at.facing, action: o.action.type, target, value: o.value });
    }
  }
  return out;
}

// A bot's settings: how many of its best options it checks against the enemy's best reply (0: none),
// how many quiet end states per unit it scores in full, and how many per aim (Infinity: all).
// `explore`: the share of activation choices made at random among the best three (for data collection;
// drawn from the game's dice state, so games still repeat exactly).
// `weights`: score weights to play with instead of today's (fitted ones), any not given as today's.
export interface BotSettings { replyK: number; trim: number; aimed?: number; explore?: number; weights?: Partial<Record<Feature, number>>; name?: string }
export const STRONG: BotSettings = { replyK: REPLY_K, trim: Infinity };
// The fast bot trims to 4 quiet end states per unit: 3.7x faster than untrimmed and stronger in paired
// games (57% over 112 pairs on the default rules); the bench's fast-k0 is the untrimmed check. It
// plays with the fitted weights (bot/weights.ts); the strong bot keeps the hand-set ones until the
// fitted ones prove themselves in its own games.
export const FAST: BotSettings = { replyK: 0, trim: 4, weights: FITTED as Partial<Record<Feature, number>>, name: FITTED_NAME };

export function getPlannerBotAction(player: CtfPlayer, s: CtfState): CtfAction | null {
  return botAction(player, s, STRONG);
}

// The fast bot: the planner without the enemy-reply check (most of its cost). For tests, baselines
// and an easier opponent.
export function getFastBotAction(player: CtfPlayer, s: CtfState): CtfAction | null {
  return botAction(player, s, FAST);
}

// A bot with other settings (the bench names them fast-k8, strong-k8, ...).
export function makeBot(settings: BotSettings): (player: CtfPlayer, s: CtfState) => CtfAction | null {
  return (player, s) => botAction(player, s, settings);
}

function botAction(player: CtfPlayer, s: CtfState, settings: BotSettings): CtfAction | null {
  const bot = `${settings.replyK}/${settings.trim}/${settings.aimed ?? Infinity}/${settings.explore ?? 0}/${settings.name ?? ''}`;
  const legal = listLegalActions(s, player.id);
  if (!legal.length) return null;
  const team = teamOf(s, player.id)!;
  const caches = newCaches(settings.weights ? { ...WEIGHT, ...settings.weights } : WEIGHT);
  if (s.pending) {
    let best = legal[0], bestV = -Infinity;
    for (const a of legal) {
      const v = actionValue(s, a, team, caches);
      if (v > bestV) { bestV = v; best = a; }
    }
    return best;
  }
  if (s.active) {
    const next = followPlan(s, team, player.id, bot);
    if (next) return next;
  }
  const o = decide(s, team, caches, settings.replyK, settings.trim, settings.aimed ?? Infinity, settings.explore ?? 0);
  if (!o) return legal.find(x => x.type === 'endActivation') ?? legal[0];
  plans[team] = { rng: s.rng, bot, round: s.round, unitId: o.unitId, pre: o.pre, startMoves: o.startMoves, start: o.start, path: o.path, action: o.action };
  if (!s.active) return { type: 'activate', playerId: player.id, unitId: o.unitId };
  if (o.pre.length) return { ...o.pre[0], playerId: player.id } as CtfAction;
  return o.path.length ? stepAction(o.path[0], player.id) : o.action;
}

// Any legal action; for smoke tests.
export function getRandomBotAction(state: CtfState, playerId: string): CtfAction | null {
  const legal = listLegalActions(state, playerId);
  if (!legal.length) return null;
  const [i] = nextRng(state.rng ^ Math.imul(state.log.length + 1, 0x9E3779B1), 0, legal.length - 1);
  return legal[i];
}

